/** Events that mean the user is scrolling a list themselves. */
const USER_INTENT_EVENTS = ["wheel", "touchstart", "touchmove", "keydown", "mousedown"] as const;

/** How long new content is followed before a list stops scrolling down on its own. */
const FOLLOW_CONTENT_TIMEOUT_MS = 500;

export class DomHelper {
  static scrollToBottom(element: HTMLElement): void {
    element.scrollTop = element.scrollHeight;
  }

  /**
   * Whether a scroll container currently sits at its bottom. Used to decide if new content
   * may be followed, or if the user scrolled up and has to keep their position.
   */
  static isScrolledToBottom(element: HTMLElement, toleranceInPx = 24): boolean {
    return element.scrollHeight - element.scrollTop - element.clientHeight <= toleranceInPx;
  }

  /**
   * Scrolls a container to the bottom immediately and again after images load and layout
   * settles. Stops as soon as the user takes the scroll position over (wheel, touch, keyboard
   * or dragging the scrollbar up), so scrolling up while attachments are still loading is
   * never undone. Returns a cleanup function to cancel pending listeners.
   */
  static scrollToBottomWhenContentSettled(element: HTMLElement): () => void {
    const pendingImages = Array.from(element.querySelectorAll<HTMLImageElement>("img"))
      .filter(image => !image.complete);

    let following = true;
    /** Position the last programmatic scroll left behind, to tell it apart from a user scroll. */
    let lastScrollTop = -1;
    let settleTimeout: number | null = null;

    const scroll = () => {
      if (!following) {
        return;
      }
      DomHelper.scrollToBottom(element);
      lastScrollTop = element.scrollTop;
    };

    const onImageSettled = () => scroll();

    const stopFollowing = () => {
      if (!following) {
        return;
      }
      following = false;

      if (settleTimeout !== null) {
        window.clearTimeout(settleTimeout);
        settleTimeout = null;
      }
      element.removeEventListener("scroll", onScroll);
      for (const userIntentEvent of USER_INTENT_EVENTS) {
        element.removeEventListener(userIntentEvent, stopFollowing);
      }
      for (const image of pendingImages) {
        image.removeEventListener("load", onImageSettled);
        image.removeEventListener("error", onImageSettled);
      }
    };

    const onScroll = () => {
      // Growing content (a loaded image) only ever pushes the position down, so an upward move
      // is the user scrolling — including a scrollbar drag, which fires no other input event.
      if (element.scrollTop < lastScrollTop - 1) {
        stopFollowing();
        return;
      }
      lastScrollTop = element.scrollTop;
    };

    const onSettleTimeout = () => {
      settleTimeout = null;
      scroll();
      // Only images that are still loading can move the content further down; without them
      // there is nothing left to follow and the list is released to the user right away.
      if (pendingImages.every(image => image.complete)) {
        stopFollowing();
      }
    };

    scroll();
    requestAnimationFrame(() => {
      scroll();
      requestAnimationFrame(scroll);
    });

    element.addEventListener("scroll", onScroll, { passive: true });
    for (const userIntentEvent of USER_INTENT_EVENTS) {
      element.addEventListener(userIntentEvent, stopFollowing, { passive: true });
    }

    for (const image of pendingImages) {
      image.addEventListener("load", onImageSettled);
      image.addEventListener("error", onImageSettled);
    }

    settleTimeout = window.setTimeout(onSettleTimeout, FOLLOW_CONTENT_TIMEOUT_MS);

    return stopFollowing;
  }
}
